'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Globe,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Menu,
  X,
  Star,
  ArrowRight,
  Shield,
  Smartphone,
  BarChart3,
  Headphones,
  Zap,
  Award,
  TrendingUp,
  GraduationCap,
  PiggyBank,
  Trophy,
  Sparkles,
  Building2,
  Users,
  Apple,
  Play,
} from 'lucide-react';

/* ───────────────────────── DATA ───────────────────────── */

const tickers = [
  { symbol: 'Apple', price: '189.40', change: '+0.55%' },
  { symbol: 'Tesla', price: '178.32', change: '+1.24%' },
  { symbol: 'Microsoft', price: '412.10', change: '+0.31%' },
  { symbol: 'Amazon', price: '184.20', change: '-0.18%' },
  { symbol: 'S&P 500', price: '5,842.3', change: '+0.32%' },
  { symbol: 'EUR/USD', price: '1.0847', change: '+0.12%' },
  { symbol: 'Gold', price: '2,987.50', change: '+0.45%' },
  { symbol: 'BTC/USD', price: '87,245', change: '+2.34%' },
  { symbol: 'FTSE 100', price: '7,892', change: '+0.18%' },
  { symbol: 'NVIDIA', price: '875.28', change: '+3.15%' },
];

const stats = [
  { value: '1.6M+', label: 'Clients worldwide' },
  { value: '6,400+', label: 'Investment instruments' },
  { value: '20+', label: 'Years of experience' },
  { value: '0%', label: 'Commission on stocks*' },
];

const investments = [
  {
    name: 'Stocks',
    count: '5,500+',
    desc: 'Real shares from global exchanges',
    icon: TrendingUp,
    gradient: 'from-[#0B2244] to-[#1E3A5F]',
  },
  {
    name: 'ETFs',
    count: '1,400+',
    desc: 'Diversify with thematic baskets',
    icon: PiggyBank,
    gradient: 'from-[#1E3A5F] to-[#0B2244]',
  },
  {
    name: 'Forex',
    count: '70+ pairs',
    desc: "Trade the world's currencies",
    icon: Globe,
    gradient: 'from-[#0B2244] to-[#0E2D4D]',
  },
  {
    name: 'Indices',
    count: '30+',
    desc: 'Track global benchmarks',
    icon: BarChart3,
    gradient: 'from-[#0E2D4D] to-[#0B2244]',
  },
  {
    name: 'Commodities',
    count: '20+',
    desc: 'Gold, oil, soft commodities',
    icon: Sparkles,
    gradient: 'from-[#0B2244] to-[#1E3A5F]',
  },
  {
    name: 'Crypto',
    count: '30+',
    desc: 'Major digital assets',
    icon: Zap,
    gradient: 'from-[#1E3A5F] to-[#0E2D4D]',
  },
];

const features = [
  {
    icon: PiggyBank,
    title: '0% commission on stocks',
    desc: 'Invest in real shares and ETFs commission-free up to €100,000 monthly turnover.*',
  },
  {
    icon: Zap,
    title: 'Lightning-fast execution',
    desc: 'Average order execution under 14ms, with deep institutional liquidity behind every fill.',
  },
  {
    icon: GraduationCap,
    title: 'Free education for everyone',
    desc: 'Hundreds of articles, video courses and live webinars — included with every account.',
  },
  {
    icon: Smartphone,
    title: 'Award-winning mobile app',
    desc: 'Trade and invest from the palm of your hand with our 4.7-rated iOS and Android app.',
  },
  {
    icon: Shield,
    title: 'Capital protection',
    desc: 'Funds segregated at top-tier banks, with negative balance protection on every account.',
  },
  {
    icon: Headphones,
    title: 'Real human support',
    desc: 'Speak to dedicated specialists in your language — no chatbots, no waiting queues.',
  },
];

const candles = [
  { o: 72, h: 78, l: 68, c: 76 },
  { o: 76, h: 82, l: 74, c: 80 },
  { o: 80, h: 85, l: 78, c: 74 },
  { o: 74, h: 79, l: 70, c: 77 },
  { o: 77, h: 84, l: 75, c: 82 },
  { o: 82, h: 88, l: 80, c: 86 },
  { o: 86, h: 90, l: 83, c: 84 },
  { o: 84, h: 89, l: 82, c: 88 },
  { o: 88, h: 94, l: 86, c: 92 },
  { o: 92, h: 96, l: 88, c: 90 },
  { o: 90, h: 95, l: 87, c: 94 },
  { o: 94, h: 100, l: 92, c: 98 },
  { o: 98, h: 102, l: 93, c: 96 },
  { o: 96, h: 101, l: 94, c: 100 },
  { o: 100, h: 106, l: 98, c: 104 },
  { o: 104, h: 108, l: 100, c: 102 },
  { o: 102, h: 107, l: 99, c: 106 },
  { o: 106, h: 112, l: 104, c: 110 },
];

const regulations = [
  { code: 'UK', name: 'FCA', full: 'Financial Conduct Authority' },
  { code: 'PL', name: 'KNF', full: 'Polish Financial Supervision Authority' },
  { code: 'CY', name: 'CySEC', full: 'Cyprus Securities and Exchange Commission' },
  { code: 'AE', name: 'DFSA', full: 'Dubai Financial Services Authority' },
  { code: 'BZ', name: 'FSC', full: 'Belize Financial Services Commission' },
  { code: 'ZA', name: 'FSCA', full: 'South Africa Financial Sector Conduct Authority' },
];

const reviews = [
  {
    name: 'Marcus Eriksson',
    role: 'Long-term investor',
    avatar: 'ME',
    text: 'I moved my entire portfolio here last year. The 0% commission on real shares is genuinely 0 — no hidden custody fees, no spread tricks. The platform is clean and the app works flawlessly.',
    stars: 5,
    source: 'Trustpilot',
  },
  {
    name: 'Priya Raman',
    role: 'ETF investor',
    avatar: 'PR',
    text: 'The breadth of ETFs is exceptional. I can build a globally diversified portfolio without leaving the platform, and the fractional shares feature lets me dollar-cost average exactly the way I want.',
    stars: 5,
    source: 'Trustpilot',
  },
  {
    name: 'Lukas Becker',
    role: 'Active trader',
    avatar: 'LB',
    text: 'Execution speed is what brought me from a traditional bank. The chart engine is responsive, the spreads are competitive, and the mobile app keeps full feature parity with desktop.',
    stars: 5,
    source: 'Google',
  },
  {
    name: 'Elena Russo',
    role: 'Beginner investor',
    avatar: 'ER',
    text: "The education library is what made the difference for me. I started with zero knowledge — now I'm comfortable building my own portfolio. The free webinars are incredibly high quality.",
    stars: 5,
    source: 'Trustpilot',
  },
  {
    name: 'Ahmed Hassan',
    role: 'CFD trader',
    avatar: 'AH',
    text: 'I trade indices and gold daily. The CFD spreads are tight, leverage is flexible, and the risk tools (negative balance protection, custom margin alerts) are genuinely useful in volatile markets.',
    stars: 5,
    source: 'Google',
  },
  {
    name: 'Sara Lindqvist',
    role: 'Wealth builder',
    avatar: 'SL',
    text: 'Best decision I made was opening an investment plan here. Auto-invest on a basket of ETFs every month, no friction, no surprise costs. I check the app once a quarter and let it compound.',
    stars: 5,
    source: 'Trustpilot',
  },
];

const awards = [
  { year: '2025', title: 'Best Investment App', source: 'Investment Trends' },
  { year: '2024', title: 'Most Trusted Broker', source: 'Global Finance' },
  { year: '2024', title: 'Best Trading Platform', source: 'Forex Awards' },
  { year: '2023', title: 'Broker of the Year', source: 'FinanceFeeds' },
];

const faqs = [
  {
    q: 'What is XTB?',
    a: "XTB is a regulated investment firm offering access to over 6,400 financial instruments — including real stocks, ETFs, forex pairs, indices, commodities and cryptocurrencies. We've been helping people invest and trade for over 20 years.",
  },
  {
    q: 'How do I open an account?',
    a: "Click 'Create account', complete a short online form with your personal details, verify your identity and fund the account. Most clients are ready to invest in under 10 minutes.",
  },
  {
    q: 'Is there a minimum deposit?',
    a: "There is no minimum deposit to open an account. You can start investing from as little as $1 in fractional shares, or fund your account with any amount you're comfortable with.",
  },
  {
    q: 'Are my funds safe?',
    a: 'Yes. Client funds are held in segregated accounts at top-tier banks, fully separated from company funds. We are regulated by the FCA, KNF, CySEC and other top-tier authorities worldwide.',
  },
  {
    q: 'What fees do you charge?',
    a: '0% commission on real shares and ETFs up to €100,000 of monthly turnover (0.2% applies above that). For CFDs, we charge transparent, competitive spreads with no hidden fees.',
  },
  {
    q: 'Can I use the app on my phone?',
    a: "Yes — our mobile app is available for iOS and Android, with full feature parity to the desktop platform. It's consistently rated above 4.7 stars on both stores.",
  },
];

const footerLinks: Record<string, { label: string; href: string }[]> = {
  Invest: [
    { label: 'Stocks', href: '#' },
    { label: 'ETFs', href: '#' },
    { label: 'Investment plans', href: '#' },
    { label: 'Fractional shares', href: '#' },
  ],
  Trade: [
    { label: 'Forex', href: '#' },
    { label: 'Indices', href: '#' },
    { label: 'Commodities', href: '#' },
    { label: 'Crypto CFDs', href: '#' },
  ],
  Platforms: [
    { label: 'Web platform', href: '#' },
    { label: 'Desktop', href: '#' },
    { label: 'Mobile app', href: '#' },
    { label: 'MetaTrader 4', href: '#' },
  ],
  Education: [
    { label: 'Trading academy', href: '#' },
    { label: 'Webinars', href: '#' },
    { label: 'Market analysis', href: '#' },
    { label: 'Glossary', href: '#' },
  ],
  About: [
    { label: 'About us', href: '#' },
    { label: 'Careers', href: '#' },
    { label: 'Press', href: '#' },
    { label: 'Contact', href: '#' },
  ],
};

/* ───────────────────────── HELPERS ───────────────────────── */

function CandlestickChart() {
  const w = 22,
    gap = 2;
  return (
    <svg
      viewBox={`0 0 ${candles.length * (w + gap)} 130`}
      className="w-full h-full"
      preserveAspectRatio="none"
    >
      {[30, 60, 90].map((y) => (
        <line
          key={y}
          x1="0"
          y1={y}
          x2={candles.length * (w + gap)}
          y2={y}
          stroke="#FFFFFF"
          strokeOpacity="0.06"
          strokeWidth="0.5"
          strokeDasharray="3 3"
        />
      ))}
      {candles.map((c, i) => {
        const x = i * (w + gap);
        const isGreen = c.c >= c.o;
        const bodyTop = 130 - Math.max(c.o, c.c);
        const bodyH = Math.abs(c.c - c.o) || 1;
        const wickTop = 130 - c.h;
        const wickBot = 130 - c.l;
        const color = isGreen ? '#10B981' : '#EF4444';
        return (
          <g key={i}>
            <line
              x1={x + w / 2}
              y1={wickTop}
              x2={x + w / 2}
              y2={wickBot}
              stroke={color}
              strokeWidth="1.5"
            />
            <rect x={x + 2} y={bodyTop} width={w - 4} height={bodyH} fill={color} rx="1" />
          </g>
        );
      })}
      <path
        d={`M0,${130 - candles[0].c} ${candles.map((c, i) => `L${i * (w + gap) + w / 2},${130 - c.c}`).join(' ')}`}
        fill="none"
        stroke="#F5A623"
        strokeWidth="1.5"
        opacity="0.5"
        strokeDasharray="3 3"
      />
    </svg>
  );
}

/* ───────────────────────── PAGE ───────────────────────── */

export default function XTBPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const reviewsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', h, { passive: true });
    return () => window.removeEventListener('scroll', h);
  }, []);

  const scrollReviews = (dir: number) => {
    if (reviewsRef.current) reviewsRef.current.scrollBy({ left: dir * 360, behavior: 'smooth' });
  };

  const navLinks = [
    { label: 'Invest', href: '#invest' },
    { label: 'Trade', href: '#trade' },
    { label: 'Platforms', href: '#platforms' },
    { label: 'Education', href: '#education' },
    { label: 'About', href: '#about' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* ── Back to gallery ── */}
      <a
        href="/templates/"
        className="fixed bottom-5 left-1/2 -translate-x-1/2 md:bottom-auto md:top-4 md:left-4 md:translate-x-0 z-[200] flex items-center gap-1.5 bg-black/70 hover:bg-black/90 backdrop-blur-sm text-white text-xs font-semibold px-4 py-2 rounded-full transition-all duration-200 shadow-xl border border-white/10"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M19 12H5M12 5l-7 7 7 7" />
        </svg>
        <span className="hidden sm:inline">All Templates</span>
        <span className="sm:hidden">Templates</span>
      </a>

      {/* ── HEADER ── */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-white/98 backdrop-blur-md shadow-sm border-b border-[#E5E7EB]'
            : 'bg-white border-b border-transparent'
        }`}
      >
        <div className="site-container flex items-center justify-between h-[72px]">
          <Link href="/templates/xtb" className="flex items-center gap-2.5">
            <div className="flex items-center gap-1">
              <div className="w-9 h-9 rounded-lg bg-[#0B2244] flex items-center justify-center">
                <span className="text-white font-bold text-sm">X</span>
              </div>
              <span className="text-[22px] font-bold text-[#0B2244] tracking-tight">XTB</span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {navLinks.map((l) => (
              <a
                key={l.label}
                href={l.href}
                className="text-[14px] font-semibold text-[#0B2244] hover:text-[#F5A623] transition-colors flex items-center gap-1"
              >
                {l.label}
                <ChevronDown className="w-3 h-3 opacity-50" />
              </a>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <span className="flex items-center gap-1.5 text-[#475569] text-xs font-medium">
              <Globe className="w-3.5 h-3.5" /> EN
            </span>
            <Link
              href="#"
              className="text-[13px] font-semibold text-[#0B2244] hover:text-[#F5A623] px-3 py-2"
            >
              Log in
            </Link>
            <Link
              href="#"
              className="bg-[#0B2244] hover:bg-[#082040] text-white text-[13px] font-bold px-6 py-2.5 rounded-full transition-colors"
            >
              Create account
            </Link>
          </div>

          <button
            className="lg:hidden p-2 text-[#0B2244]"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="lg:hidden bg-white border-t border-[#E5E7EB] overflow-hidden"
            >
              <div className="site-container py-4 flex flex-col gap-1">
                {navLinks.map((l) => (
                  <a
                    key={l.label}
                    href={l.href}
                    className="text-sm text-[#0B2244] font-semibold py-3 border-b border-[#F1F5F9]"
                    onClick={() => setMobileOpen(false)}
                  >
                    {l.label}
                  </a>
                ))}
                <Link
                  href="#"
                  className="bg-[#0B2244] text-white text-sm font-bold py-3 rounded-full text-center mt-3"
                >
                  Create account
                </Link>
                <Link href="#" className="text-center text-sm font-semibold text-[#0B2244] py-3">
                  Log in
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* ═══════════════════════════════════════════════════════ */}
      {/* SECTION 1 — HERO with ambassador image */}
      {/* ═══════════════════════════════════════════════════════ */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0B2244] via-[#0E2D4D] to-[#1E3A5F]">
        {/* Decorative blur */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#F5A623]/15 rounded-full blur-3xl -translate-y-1/4 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#F5A623]/10 rounded-full blur-3xl translate-y-1/4" />

        <div className="site-container relative z-10 py-12 md:py-16 lg:py-20">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            {/* Left — text */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-7"
            >
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-6">
                <Trophy className="w-3.5 h-3.5 text-[#F5A623]" />
                <span className="text-white/90 text-[11px] font-bold tracking-wider uppercase">
                  Most awarded broker · 2025
                </span>
              </div>
              <h1 className="text-[44px] md:text-[64px] lg:text-[78px] font-bold text-white leading-[1.02] tracking-tight mb-6">
                Invest in
                <br />
                <span className="text-[#F5A623]">what moves you.</span>
              </h1>
              <p className="text-white/75 text-lg md:text-xl mb-8 max-w-xl leading-relaxed">
                Invest in <strong className="text-white">real stocks and ETFs</strong> with 0%
                commission, or trade <strong className="text-white">6,400+ markets</strong> on a
                single regulated platform.
              </p>
              <div className="flex flex-wrap gap-3 mb-10">
                <Link
                  href="#"
                  className="bg-[#F5A623] hover:bg-[#E0941A] text-[#0B2244] font-bold px-8 py-4 rounded-full transition-colors inline-flex items-center gap-2 shadow-lg shadow-[#F5A623]/20"
                >
                  Create account <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  href="#"
                  className="bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white font-bold px-8 py-4 rounded-full hover:bg-white hover:text-[#0B2244] transition-colors inline-flex items-center gap-2"
                >
                  <Play className="w-4 h-4" /> Watch demo
                </Link>
              </div>
              {/* Trust row */}
              <div className="flex flex-wrap items-center gap-6">
                <div className="flex items-center gap-2">
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div
                        key={i}
                        className="w-[18px] h-[18px] bg-[#00B67A] flex items-center justify-center"
                      >
                        <Star className="w-3 h-3 fill-white text-white" />
                      </div>
                    ))}
                  </div>
                  <span className="text-white/80 text-xs font-semibold">4.6 · Trustpilot</span>
                </div>
                <div className="hidden md:block w-px h-5 bg-white/20" />
                <div className="flex items-center gap-2 text-white/70 text-xs">
                  <Award className="w-4 h-4 text-[#F5A623]" />
                  <span>
                    <strong className="text-white">1.6M+</strong> investors trust us
                  </span>
                </div>
                <div className="hidden md:block w-px h-5 bg-white/20" />
                <div className="flex items-center gap-2 text-white/70 text-xs">
                  <Shield className="w-4 h-4 text-[#F5A623]" />
                  <span>FCA · KNF · CySEC regulated</span>
                </div>
              </div>
            </motion.div>

            {/* Right — Ambassador image with floating cards */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="lg:col-span-5 relative h-[440px] md:h-[560px] hidden lg:block"
            >
              {/* Ambassador photo — Unsplash, confident professional */}
              <div className="absolute inset-0 flex items-end justify-center">
                <div className="relative w-full h-full">
                  {/* Subtle backdrop ring */}
                  <div className="absolute inset-x-8 top-8 bottom-0 bg-gradient-to-t from-[#F5A623]/30 to-transparent rounded-t-full blur-2xl" />
                  {/* Image */}
                  <img
                    src="https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?w=900&q=85&auto=format&fit=crop"
                    alt="Confident investor"
                    className="relative w-full h-full object-cover object-top rounded-2xl"
                    style={{
                      filter: 'contrast(1.05) saturate(1.05)',
                      WebkitMaskImage: 'linear-gradient(to bottom, black 75%, transparent 100%)',
                      maskImage: 'linear-gradient(to bottom, black 75%, transparent 100%)',
                    }}
                  />
                </div>
              </div>

              {/* Floating card 1 — Portfolio value */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
                className="absolute top-12 -left-6 bg-white rounded-xl shadow-2xl border border-[#E5E7EB] p-4 w-[200px]"
              >
                <p className="text-[10px] text-[#64748B] font-semibold uppercase tracking-wider mb-1">
                  Portfolio value
                </p>
                <p className="text-2xl font-bold text-[#0B2244]">€127,450</p>
                <div className="flex items-center gap-1 mt-1">
                  <TrendingUp className="w-3 h-3 text-[#10B981]" />
                  <span className="text-[#10B981] text-xs font-bold">+12.4%</span>
                  <span className="text-[#64748B] text-xs">this year</span>
                </div>
              </motion.div>

              {/* Floating card 2 — Live trade */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.8 }}
                className="absolute bottom-20 -right-4 bg-white rounded-xl shadow-2xl border border-[#E5E7EB] p-4 w-[210px]"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold text-[#0B2244]">AAPL · Apple Inc.</span>
                  <span className="text-[10px] bg-[#10B981]/10 text-[#10B981] px-2 py-0.5 rounded font-bold">
                    +0.55%
                  </span>
                </div>
                <p className="text-xl font-bold text-[#0B2244]">$189.40</p>
                <p className="text-[10px] text-[#64748B]">10 shares · 0% commission</p>
                <button className="mt-2 w-full bg-[#0B2244] text-white text-xs font-bold py-2 rounded-md">
                  Buy now
                </button>
              </motion.div>

              {/* Floating card 3 — Award badge */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 1 }}
                className="absolute top-1/2 -right-2 bg-gradient-to-br from-[#F5A623] to-[#E0941A] rounded-xl shadow-2xl p-3 w-[150px]"
              >
                <div className="flex items-center gap-2">
                  <Trophy className="w-7 h-7 text-white" />
                  <div>
                    <p className="text-white text-[10px] font-bold uppercase">Best app</p>
                    <p className="text-white/90 text-[10px]">2025 awards</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════ */}
      {/* SECTION 2 — STATS / AWARDS STRIP */}
      {/* ═══════════════════════════════════════════════════════ */}
      <section className="bg-white border-b border-[#E5E7EB] py-10 md:py-14">
        <div className="site-container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="text-center md:text-left"
              >
                <p className="text-3xl md:text-5xl font-bold text-[#0B2244] tracking-tight">
                  {s.value}
                </p>
                <p className="text-[#64748B] text-sm mt-1">{s.label}</p>
              </motion.div>
            ))}
          </div>
          <p className="text-[10px] text-[#94A3B8] text-center md:text-left mt-4">
            *Up to €100,000 monthly turnover. 0.2% commission applies above.
          </p>

          {/* Awards row */}
          <div className="mt-10 pt-8 border-t border-[#E5E7EB] flex flex-wrap justify-center md:justify-between items-center gap-6">
            <p className="text-[#64748B] text-xs uppercase tracking-widest font-semibold">
              Recognised internationally
            </p>
            <div className="flex flex-wrap gap-6 md:gap-10">
              {awards.map((a, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-full bg-[#F5A623]/10 flex items-center justify-center">
                    <Award className="w-4 h-4 text-[#F5A623]" />
                  </div>
                  <div>
                    <p className="text-[#0B2244] text-xs font-bold">{a.title}</p>
                    <p className="text-[#94A3B8] text-[10px]">
                      {a.source} · {a.year}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════ */}
      {/* SECTION 3 — INVESTMENTS GRID */}
      {/* ═══════════════════════════════════════════════════════ */}
      <section className="site-section bg-[#F8FAFC]" id="invest">
        <div className="site-container">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-[#F5A623] text-sm font-bold uppercase tracking-widest mb-3">
              What to invest in
            </p>
            <h2 className="text-3xl md:text-5xl font-bold text-[#0B2244] tracking-tight leading-[1.1]">
              6,400+ instruments.
              <br />
              One platform.
            </h2>
            <p className="text-[#64748B] text-lg mt-4 leading-relaxed">
              From real shares of global companies to thematic ETFs and CFDs on the world&rsquo;s
              most liquid markets.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {investments.map((m, i) => (
              <motion.a
                key={i}
                href="#"
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
                className="group relative rounded-2xl overflow-hidden bg-gradient-to-br h-[180px] p-6 flex flex-col justify-between hover:shadow-2xl hover:-translate-y-1 transition-all"
                style={{ background: `linear-gradient(135deg, #0B2244, #1E3A5F)` }}
              >
                {/* Decorative blur */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#F5A623]/10 rounded-full blur-2xl group-hover:bg-[#F5A623]/30 transition-colors" />
                <div className="relative flex items-start justify-between">
                  <m.icon className="w-9 h-9 text-[#F5A623]" />
                  <ChevronRight className="w-5 h-5 text-white/30 group-hover:text-[#F5A623] group-hover:translate-x-1 transition-all" />
                </div>
                <div className="relative">
                  <h3 className="text-2xl font-bold text-white">{m.name}</h3>
                  <p className="text-[#F5A623] text-xs font-bold mt-1">{m.count}</p>
                  <p className="text-white/60 text-sm mt-1">{m.desc}</p>
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════ */}
      {/* SECTION 4 — WHY US */}
      {/* ═══════════════════════════════════════════════════════ */}
      <section className="site-section bg-white" id="why">
        <div className="site-container">
          <div className="text-center mb-12">
            <p className="text-[#F5A623] text-sm font-bold uppercase tracking-widest mb-3">
              Why XTB
            </p>
            <h2 className="text-3xl md:text-5xl font-bold text-[#0B2244] tracking-tight">
              A broker built around you
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07 }}
                className="bg-[#F8FAFC] rounded-2xl p-7 border border-[#E5E7EB] hover:border-[#F5A623]/40 hover:shadow-md transition-all group"
              >
                <div className="w-12 h-12 rounded-xl bg-[#F5A623]/10 flex items-center justify-center mb-5 group-hover:bg-[#F5A623] transition-colors">
                  <f.icon className="w-5 h-5 text-[#F5A623] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-lg font-bold text-[#0B2244] mb-2">{f.title}</h3>
                <p className="text-[#64748B] text-sm leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════ */}
      {/* SECTION 5 — PLATFORM & APP showcase */}
      {/* ═══════════════════════════════════════════════════════ */}
      <section
        className="bg-gradient-to-br from-[#0B2244] via-[#0E2D4D] to-[#1E3A5F] py-20 md:py-24 relative overflow-hidden"
        id="platforms"
      >
        <div className="absolute top-1/2 right-0 w-[500px] h-[500px] bg-[#F5A623]/10 rounded-full blur-3xl translate-x-1/4 -translate-y-1/2" />
        <div className="site-container relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#F5A623] text-sm font-bold uppercase tracking-widest mb-3">
                The platform
              </p>
              <h2 className="text-3xl md:text-5xl font-bold text-white tracking-tight leading-[1.1] mb-5">
                Trade and invest
                <br />
                from anywhere.
              </h2>
              <p className="text-white/70 text-lg leading-relaxed mb-8">
                Our flagship web platform, desktop terminal and award-winning mobile app — all share
                a single account and synchronise in real time.
              </p>
              <div className="space-y-3 mb-8">
                {[
                  'Advanced charting with 100+ indicators',
                  'One-click trading and execution',
                  'Real-time prices and depth-of-market',
                  'Built-in risk management tools',
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.08 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-5 h-5 rounded-full bg-[#F5A623] flex items-center justify-center flex-shrink-0">
                      <svg
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#0B2244"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </div>
                    <span className="text-white text-sm">{item}</span>
                  </motion.div>
                ))}
              </div>
              <div className="flex flex-wrap gap-3">
                <Link
                  href="#"
                  className="bg-black hover:bg-[#1A1A1A] text-white font-bold px-5 py-3 rounded-xl transition-colors inline-flex items-center gap-3 border border-white/10"
                >
                  <Apple className="w-6 h-6" />
                  <div className="text-left">
                    <p className="text-[10px] opacity-70">Download on the</p>
                    <p className="text-sm">App Store</p>
                  </div>
                </Link>
                <Link
                  href="#"
                  className="bg-black hover:bg-[#1A1A1A] text-white font-bold px-5 py-3 rounded-xl transition-colors inline-flex items-center gap-3 border border-white/10"
                >
                  <Play className="w-6 h-6 fill-white" />
                  <div className="text-left">
                    <p className="text-[10px] opacity-70">Get it on</p>
                    <p className="text-sm">Google Play</p>
                  </div>
                </Link>
              </div>
            </div>

            {/* Right — phone + desktop mockup */}
            <div className="relative h-[500px]">
              {/* Desktop chart */}
              <div className="absolute top-0 left-0 right-12 bg-[#0B1729] rounded-2xl border border-white/10 overflow-hidden shadow-2xl">
                <div className="flex items-center justify-between px-5 py-3 border-b border-white/5">
                  <div className="flex items-center gap-3">
                    <span className="text-white text-sm font-bold">AAPL</span>
                    <span className="text-[#10B981] text-xs font-bold">$189.40</span>
                    <span className="text-[10px] bg-[#10B981]/15 text-[#10B981] px-2 py-0.5 rounded font-bold">
                      +0.55%
                    </span>
                  </div>
                  <div className="flex gap-1">
                    {['1H', '4H', '1D', '1W', '1M'].map((tf, i) => (
                      <span
                        key={tf}
                        className={`text-[9px] px-2 py-0.5 rounded font-medium ${i === 2 ? 'bg-[#F5A623] text-[#0B2244]' : 'text-white/40'}`}
                      >
                        {tf}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="h-[200px] p-4">
                  <CandlestickChart />
                </div>
                <div className="px-5 py-3 grid grid-cols-2 gap-3 border-t border-white/5">
                  <button className="bg-[#10B981] hover:bg-[#059669] text-white font-bold py-2.5 rounded-lg text-sm transition-colors">
                    BUY 189.41
                  </button>
                  <button className="bg-[#EF4444] hover:bg-[#DC2626] text-white font-bold py-2.5 rounded-lg text-sm transition-colors">
                    SELL 189.39
                  </button>
                </div>
              </div>

              {/* Phone mockup */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="absolute -bottom-8 right-0 w-[200px] h-[400px] bg-black rounded-[36px] border-[10px] border-[#1F2937] shadow-2xl overflow-hidden"
              >
                <div className="bg-[#0B1729] h-full p-3 flex flex-col">
                  {/* Notch */}
                  <div className="w-16 h-4 bg-black rounded-b-2xl mx-auto -mt-3 mb-3" />
                  {/* Header */}
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-white text-xs font-bold">Portfolio</span>
                    <Sparkles className="w-3 h-3 text-[#F5A623]" />
                  </div>
                  {/* Big number */}
                  <p className="text-[10px] text-white/50 uppercase tracking-wider">Total value</p>
                  <p className="text-2xl font-bold text-white">€127,450</p>
                  <p className="text-[#10B981] text-xs font-bold">+€14,210 (+12.4%)</p>
                  {/* Mini chart */}
                  <div className="h-[80px] mt-3 -mx-1">
                    <svg viewBox="0 0 200 80" className="w-full h-full">
                      <path
                        d="M0,60 L20,55 L40,58 L60,45 L80,40 L100,30 L120,35 L140,25 L160,20 L180,15 L200,10"
                        fill="none"
                        stroke="#F5A623"
                        strokeWidth="2"
                      />
                      <path
                        d="M0,60 L20,55 L40,58 L60,45 L80,40 L100,30 L120,35 L140,25 L160,20 L180,15 L200,10 L200,80 L0,80 Z"
                        fill="url(#grad1)"
                        opacity="0.3"
                      />
                      <defs>
                        <linearGradient id="grad1" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#F5A623" />
                          <stop offset="100%" stopColor="#F5A623" stopOpacity="0" />
                        </linearGradient>
                      </defs>
                    </svg>
                  </div>
                  {/* Holdings */}
                  <div className="space-y-2 mt-2">
                    {[
                      { sym: 'AAPL', val: '€32,450', chg: '+2.1%' },
                      { sym: 'MSFT', val: '€28,120', chg: '+0.8%' },
                      { sym: 'VWCE', val: '€41,980', chg: '+1.4%' },
                    ].map((h) => (
                      <div
                        key={h.sym}
                        className="flex items-center justify-between bg-white/5 rounded-lg px-2.5 py-2"
                      >
                        <span className="text-white text-[10px] font-bold">{h.sym}</span>
                        <div className="text-right">
                          <p className="text-white text-[10px] font-bold">{h.val}</p>
                          <p className="text-[#10B981] text-[9px]">{h.chg}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════ */}
      {/* SECTION 6 — REVIEWS + REGULATION */}
      {/* ═══════════════════════════════════════════════════════ */}
      <section className="site-section bg-white" id="about">
        <div className="site-container">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10">
            <div>
              <p className="text-[#F5A623] text-sm font-bold uppercase tracking-widest mb-3">
                Trusted globally
              </p>
              <h2 className="text-3xl md:text-5xl font-bold text-[#0B2244] tracking-tight">
                1.6M+ investors choose XTB
              </h2>
              <div className="flex items-center gap-4 mt-4">
                <div className="flex items-center gap-2">
                  <Star className="w-6 h-6 fill-[#00B67A] text-[#00B67A]" />
                  <span className="text-sm font-bold text-[#0B2244]">Trustpilot</span>
                  <span className="text-sm text-[#64748B]">4.6 / 5</span>
                </div>
                <div className="w-px h-5 bg-[#E5E7EB]" />
                <div className="flex items-center gap-2 text-sm text-[#64748B]">
                  <Users className="w-4 h-4" />
                  <span>22,000+ reviews</span>
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-4 md:mt-0">
              <button
                onClick={() => scrollReviews(-1)}
                className="w-10 h-10 rounded-full border border-[#E5E7EB] flex items-center justify-center text-[#64748B] hover:text-white hover:bg-[#0B2244] hover:border-[#0B2244] transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => scrollReviews(1)}
                className="w-10 h-10 rounded-full border border-[#E5E7EB] flex items-center justify-center text-[#64748B] hover:text-white hover:bg-[#0B2244] hover:border-[#0B2244] transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div
            ref={reviewsRef}
            className="flex gap-5 overflow-x-auto pb-4 mb-16"
            style={{ scrollbarWidth: 'none' }}
          >
            {reviews.map((r, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="flex-shrink-0 w-[340px] bg-[#F8FAFC] rounded-2xl border border-[#E5E7EB] p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex gap-0.5">
                    {Array.from({ length: r.stars }).map((_, si) => (
                      <div
                        key={si}
                        className="w-5 h-5 bg-[#00B67A] flex items-center justify-center"
                      >
                        <Star className="w-3 h-3 fill-white text-white" />
                      </div>
                    ))}
                  </div>
                  <span
                    className={`text-[10px] font-bold px-2 py-0.5 rounded ${r.source === 'Trustpilot' ? 'bg-[#00B67A]/10 text-[#00B67A]' : 'bg-[#FBBF24]/10 text-[#D97706]'}`}
                  >
                    {r.source}
                  </span>
                </div>
                <p className="text-[#0B2244] text-sm leading-relaxed mb-5">
                  &ldquo;{r.text}&rdquo;
                </p>
                <div className="flex items-center gap-3 border-t border-[#E5E7EB] pt-4">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#0B2244] to-[#1E3A5F] flex items-center justify-center">
                    <span className="text-white text-sm font-bold">{r.avatar}</span>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-[#0B2244]">{r.name}</p>
                    <p className="text-xs text-[#64748B]">{r.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Regulation block */}
          <div className="border-t border-[#E5E7EB] pt-14">
            <div className="text-center mb-10">
              <p className="text-[#F5A623] text-sm font-bold uppercase tracking-widest mb-3">
                Regulation
              </p>
              <h3 className="text-2xl md:text-4xl font-bold text-[#0B2244] tracking-tight">
                Authorised across the world&rsquo;s major financial centres
              </h3>
            </div>
            <div className="flex flex-wrap justify-center gap-3">
              {regulations.map((r, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="flex items-center gap-3 bg-[#F8FAFC] border border-[#E5E7EB] rounded-xl px-5 py-3 hover:border-[#F5A623]/30 transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-[#0B2244] flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-[10px] font-bold">{r.code}</span>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-[#0B2244]">{r.name}</p>
                    <p className="text-[10px] text-[#64748B] max-w-[180px]">{r.full}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════ */}
      {/* SECTION 7 — FAQ + CTA + FOOTER */}
      {/* ═══════════════════════════════════════════════════════ */}

      <section className="site-section bg-[#F8FAFC]" id="education">
        <div className="site-container">
          <div className="grid md:grid-cols-3 gap-12">
            <div>
              <p className="text-[#F5A623] text-sm font-bold uppercase tracking-widest mb-3">
                Help
              </p>
              <h2 className="text-2xl md:text-4xl font-bold text-[#0B2244] tracking-tight mb-4">
                Got questions?
              </h2>
              <p className="text-[#64748B] mb-6">
                Our specialists are available 24/5 by phone, email or live chat — in your language.
              </p>
              <Link
                href="#"
                className="inline-flex items-center gap-2 text-[#F5A623] font-bold text-sm hover:gap-3 transition-all"
              >
                Contact support <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="md:col-span-2 space-y-3">
              {faqs.map((f, i) => (
                <div
                  key={i}
                  className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between p-5 text-left"
                  >
                    <span className="font-bold text-[#0B2244] text-sm">{f.q}</span>
                    <ChevronDown
                      className={`w-5 h-5 text-[#F5A623] flex-shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`}
                    />
                  </button>
                  <AnimatePresence>
                    {openFaq === i && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <p className="px-5 pb-5 text-sm text-[#64748B] leading-relaxed">{f.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-br from-[#0B2244] to-[#1E3A5F] py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%2740%27 height=%2740%27 viewBox=%270 0 40 40%27%3E%3Cpath fill=%27%23ffffff%27 fill-opacity=%270.03%27 d=%27M0 0h40v40H0z%27/%3E%3Cpath fill=%27%23ffffff%27 fill-opacity=%270.06%27 d=%27M0 0h1v40H0zM39 0h1v40h-1zM0 0h40v1H0zM0 39h40v1H0z%27/%3E%3C/svg%3E')] opacity-50" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#F5A623]/15 rounded-full blur-3xl" />
        <div className="site-container relative">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-3xl md:text-5xl font-bold text-white tracking-tight leading-[1.1] mb-4">
                Start investing
                <br />
                in <span className="text-[#F5A623]">10 minutes.</span>
              </h2>
              <p className="text-white/70 text-lg max-w-md">
                No minimum deposit. Real shares, ETFs and 6,400+ markets — all in one regulated
                account.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 md:justify-end">
              <Link
                href="#"
                className="bg-[#F5A623] text-[#0B2244] hover:bg-[#E0941A] font-bold px-8 py-4 rounded-full transition-colors inline-flex items-center justify-center gap-2 shadow-lg shadow-[#F5A623]/20"
              >
                Create account <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                href="#"
                className="border-2 border-white/30 text-white hover:bg-white hover:text-[#0B2244] font-bold px-8 py-4 rounded-full transition-colors text-center"
              >
                Try free demo
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#081B36] pt-14 pb-6">
        <div className="site-container">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 mb-12">
            {Object.entries(footerLinks).map(([cat, links]) => (
              <div key={cat}>
                <h4 className="text-white font-bold text-sm mb-4">{cat}</h4>
                <ul className="space-y-2.5">
                  {links.map((l) => (
                    <li key={l.label}>
                      <a
                        href={l.href}
                        className="text-white/50 text-xs hover:text-[#F5A623] transition-colors"
                      >
                        {l.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="border-t border-white/10 pt-8 mb-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-lg bg-[#F5A623] flex items-center justify-center">
                <span className="text-[#0B2244] font-bold text-sm">X</span>
              </div>
              <span className="text-white font-bold text-lg">XTB</span>
            </div>
            <div className="flex items-center gap-5 text-white/50 text-xs">
              <span className="flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5" /> Warsaw · London · Madrid · Dubai · Mexico City
              </span>
            </div>
          </div>

          <div className="border-t border-white/5 pt-6">
            <p className="text-[10px] text-white/40 leading-relaxed mb-3">
              <strong className="text-white/70">Risk warning:</strong> CFDs are complex instruments
              and come with a high risk of losing money rapidly due to leverage.{' '}
              <strong className="text-white/70">
                76% of retail investor accounts lose money when trading CFDs with this provider.
              </strong>{' '}
              You should consider whether you understand how CFDs work and whether you can afford to
              take the high risk of losing your money. Past performance does not guarantee future
              results.
            </p>
            <div className="flex flex-wrap gap-4 text-[10px] text-white/40 mt-3">
              <a href="#" className="hover:text-white transition-colors">
                Terms & conditions
              </a>
              <a href="#" className="hover:text-white transition-colors">
                Privacy policy
              </a>
              <a href="#" className="hover:text-white transition-colors">
                Risk disclosure
              </a>
              <a href="#" className="hover:text-white transition-colors">
                Cookie policy
              </a>
              <a href="#" className="hover:text-white transition-colors">
                Order execution
              </a>
            </div>
            <p className="text-[10px] text-white/30 mt-4">
              &copy; 2026 XTB Group. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      {/* ── BOTTOM TICKER ── */}
      <div className="bg-[#081B36] border-t border-white/5 py-2.5 overflow-hidden">
        <div className="ticker-animate flex gap-8 whitespace-nowrap">
          {[...tickers, ...tickers].map((t, i) => (
            <span key={i} className="inline-flex items-center gap-2 text-xs">
              <span className="font-bold text-white/70">{t.symbol}</span>
              <span className="text-white/50">{t.price}</span>
              <span className={t.change.startsWith('+') ? 'text-[#10B981]' : 'text-[#EF4444]'}>
                {t.change}
              </span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
